import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { MatInputModule } from '@angular/material/input';
import { FilterComponent } from '../filter.interfaces';
import { FilterService } from '../filter.service';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import {
  FilterCommand,
  FilterOperator,
  getCommandOperator,
} from '../../../api/api.operators';
import { FilterOperatorComponent } from '../filter-operator/filter-operator.component';

@Component({
  selector: 'app-text-filter',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    ReactiveFormsModule,
    MatIcon,
    FilterOperatorComponent,
  ],
  providers: [],
  templateUrl: './text-filter.component.html',
})
export class TextFilterComponent implements FilterComponent, OnInit {
  appFilter = input.required<string>();
  appFilterOperators = input<FilterOperator[]>();
  appFilterNotOperators = input<FilterOperator[]>();
  filterCommand = signal<FilterCommand>(FilterOperator.LIKE);
  filterOperator = computed(() => getCommandOperator(this.filterCommand()));
  filterControl = new FormControl('', { nonNullable: true });

  constructor(
    private readonly filterService: FilterService,
    private readonly destroyRef: DestroyRef,
  ) {
    effect(() => {
      if (
        this.filterControl.enabled &&
        this.filterOperator() == FilterOperator.NULL
      )
        this.filterControl.disable();
      else if (this.filterControl.disabled) this.filterControl.enable();
    });
  }

  ngOnInit() {
    const filter = this.appFilter();
    const initialValue = this.filterService.getFilter(filter);
    const operator = this.filterService.getFilterOperator(
      filter,
      this.appFilterOperators()?.[0],
    );
    this.filterCommand.set(operator);
    this.filterControl.patchValue(initialValue);

    this.filterControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const filter = this.appFilter();
        const operatorTokens = this.filterCommand();
        const [suffix, operator] = Array.isArray(operatorTokens)
          ? operatorTokens
          : [undefined, operatorTokens];
        if (suffix)
          this.filterService.setFilter(filter, value, operator, suffix);
        else this.filterService.setFilter(filter, value, operator);
      });
  }

  operatorChanged(command: FilterCommand) {
    this.filterCommand.set(command);
    const filter = this.appFilter();
    const [suffix, operator] = Array.isArray(command)
      ? command
      : [undefined, command];
    if (suffix)
      this.filterService.setFilter(
        filter,
        this.filterControl.value,
        operator,
        suffix,
      );
    else
      this.filterService.setFilter(filter, this.filterControl.value, operator);
  }

  empty() {
    this.filterControl.setValue('');
  }

  protected readonly FilterOperator = FilterOperator;
}
